import { useCallback, useEffect, useState } from 'react'
import { api } from '../../servizi/api.ts'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Spunta, Tabella } from '../../componenti/base.tsx'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { messaggioErrore } from '../../componenti/elenco.tsx'
import { IconaPiu } from '../../componenti/Icone.tsx'
import { NOMI_TIPO_STANZA, type Stanza } from '../../componenti/stanze.tsx'

/**
 * Linguetta «Stanze» della pagina della sede (558; docs/15-calendari-sedi.md, fase 3).
 *
 * Stanze, sale, postazioni e attrezzature della sede, con capienza, accessibilita' e se sono adatte ai
 * colloqui. Le leggono tutti; le modifica chi ha `sede.agenda` sulla sede. Una stanza non si cancella:
 * si mette «non in uso», perche' mediazioni, appuntamenti e riunioni passati la nominano. Una stanza
 * occupata blocca: nell'agenda, nelle mediazioni e negli appuntamenti non si fissa un secondo impegno.
 */

type RispostaStanze = { sede: { id: string; nome: string }; stanze: Stanza[]; puo_scrivere: boolean }

export function Stanze({ sedeId }: { sedeId: string }) {
  const [dati, setDati] = useState<RispostaStanze | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [aperta, setAperta] = useState<Stanza | 'nuova' | null>(null)
  const [nonInUso, setNonInUso] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)

  const carica = useCallback(() => {
    api.get<RispostaStanze>(`/sedi/${sedeId}/stanze`).then(setDati).catch((e) => setErrore(messaggioErrore(e)))
  }, [sedeId])
  useEffect(carica, [carica])

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  if (!dati) return <Attesa />
  const ferme = dati.stanze.filter((s) => !s.attiva).length
  const stanze = dati.stanze.filter((s) => nonInUso || s.attiva)

  return (
    <>
      {!dati.puo_scrivere && (
        <p className="tenue">Le stanze le aggiunge e le modifica chi coordina la sede.</p>
      )}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      <Riquadro
        titolo="Stanze, sale e postazioni"
        azioni={<>
          {ferme > 0 && <Spunta etichetta={`Mostra quelle non in uso (${ferme})`} valore={nonInUso} cambia={setNonInUso} />}
          {dati.puo_scrivere && (
            <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />} onClick={() => setAperta('nuova')}>
              Aggiungi
            </Bottone>
          )}
        </>}
        denso
      >
        <Tabella intestazioni={['Nome', 'Tipo', 'Posti', 'Caratteristiche', 'Nota', '']}
                 vuoto={<Vuoto testo="Nessuna stanza. Aggiungile per prenotarle e assegnarle a mediazioni e appuntamenti." />}>
          {stanze.map((s) => (
            <tr key={s.id}>
              <td data-etichetta="Nome">
                {s.nome}{!s.attiva && <> <Etichetta>non in uso</Etichetta></>}
              </td>
              <td data-etichetta="Tipo">{NOMI_TIPO_STANZA[s.tipo]}</td>
              <td data-etichetta="Posti">{s.capienza ?? ''}</td>
              <td data-etichetta="Caratteristiche">
                {[s.accessibile ? 'accessibile' : null, s.adatta_colloqui ? 'adatta ai colloqui' : null,
                  s.prenotabile ? null : 'non si prenota'].filter(Boolean).join(', ')}
              </td>
              <td data-etichetta="Nota">{s.nota}</td>
              <td>
                {dati.puo_scrivere && (
                  <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAperta(s)}>Modifica</Bottone>
                )}
              </td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>
      {aperta && (
        <ModuloStanza sedeId={sedeId} stanza={aperta === 'nuova' ? null : aperta} chiudi={() => setAperta(null)}
                      fatto={(testo) => { setAperta(null); setEsito(testo); carica() }} />
      )}
    </>
  )
}

function ModuloStanza({ sedeId, stanza, chiudi, fatto }: {
  sedeId: string; stanza: Stanza | null; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [nome, setNome] = useState(stanza?.nome ?? '')
  const [tipo, setTipo] = useState<Stanza['tipo']>(stanza?.tipo ?? 'stanza')
  const [capienza, setCapienza] = useState(stanza?.capienza ? String(stanza.capienza) : '')
  const [accessibile, setAccessibile] = useState(stanza?.accessibile ?? false)
  const [colloqui, setColloqui] = useState(stanza?.adatta_colloqui ?? false)
  const [prenotabile, setPrenotabile] = useState(stanza?.prenotabile ?? true)
  const [nota, setNota] = useState(stanza?.nota ?? '')
  const [attiva, setAttiva] = useState(stanza?.attiva ?? true)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function salva() {
    setErrore(null); setInCorso(true)
    const corpo = {
      nome: nome.trim(), tipo, capienza: capienza ? Number(capienza) : null, accessibile, adatta_colloqui: colloqui,
      prenotabile, nota: nota.trim() || null, ...(stanza ? { attiva } : {}),
    }
    try {
      if (stanza) {
        await api.patch(`/sedi/${sedeId}/stanze/${stanza.id}`, corpo)
        fatto(attiva ? `«${corpo.nome}» aggiornata.` : `«${corpo.nome}» non è più in uso: non si sceglie più per gli impegni nuovi.`)
      } else {
        await api.post(`/sedi/${sedeId}/stanze`, corpo)
        fatto(`«${corpo.nome}» aggiunta.`)
      }
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={stanza ? 'Modifica la stanza' : 'Aggiungi una stanza'} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={!nome.trim()} onClick={() => void salva()}>
                {stanza ? 'Salva' : 'Aggiungi'}
              </Bottone>
            </>}>
      <div className="griglia-modulo">
        <Campo etichetta="Nome" obbligatorio valore={nome} cambia={setNome} maxLength={80} segnaposto="Per esempio: Sala 1" />
        <Scelta etichetta="Tipo" valore={tipo} cambia={(v) => setTipo(v as Stanza['tipo'])}
                opzioni={Object.entries(NOMI_TIPO_STANZA).map(([valore, etichetta]) => ({ valore, etichetta }))} />
        <Campo etichetta="Posti" tipo="number" valore={capienza} cambia={setCapienza} min={1} max={500} />
      </div>
      <Spunta etichetta="Accessibile a chi ha difficoltà motorie" valore={accessibile} cambia={setAccessibile} />
      <Spunta etichetta="Adatta ai colloqui riservati" valore={colloqui} cambia={setColloqui}
              aiuto="Per mediazioni e appuntamenti la proposta la mette in cima." />
      <Spunta etichetta="Si prenota dall’agenda" valore={prenotabile} cambia={setPrenotabile}
              aiuto="Senza, si assegna a mediazioni, appuntamenti e riunioni, ma non compare fra le stanze da prenotare." />
      <Campo etichetta="Nota" valore={nota} cambia={setNota} maxLength={200}
             aiuto="La leggono tutti quelli che usano il gestionale: non scrivere nomi di persone." />
      {stanza && (
        <Spunta etichetta="In uso" valore={attiva} cambia={setAttiva}
                aiuto="Una stanza non si cancella: tolta dall’uso, resta negli impegni passati." />
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
